import './buchen.css';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { ConfirmDialog } from '../../components/Dialog';
import { monthName, today } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { BelegDialog } from '../belege/BelegDialog';
import { useKanzlei } from '../context';
import { KiHinweis } from '../KiHinweis';
import { BuchenTabelle, RechnungenTabelle } from './BuchenTabelle';
import { ExportLeiste } from './ExportLeiste';
import { ZeileFormular, type ZeileStart, type ZeileWerte } from './HandZeile';
import { AnfangKarte, BankkontoKarte, DateiHinweise, DateienListe, KontoAmpeln, NichtBuchen } from './Karten';
import { useKontenliste } from './kontenliste';
import {
  entwuerfeLesen,
  entwuerfeText,
  entwurfAus,
  entwurfGleich,
  entwurfSchluessel,
  erwartetAus,
  imFilter,
  monatSchritt,
  naechsteOffene,
  vollstaendig,
  zuEingabe,
  zuPruefen,
  type EntwurfTeil,
  type Filter,
} from './logik';
import type { ErwartetTeil, MonatAntwort, TeilEingabe, ZeileSicht } from './types';
import { ZeileDetail } from './ZeileDetail';

// Seite „Buchen“ (Kanzlei › Mandant › Belege › Monat › „Buchen“): Kontoauszug des Monats Zeile für Zeile bestätigen,
// danach „CSV für ADDISON“ und „DATEV-Format“. Bauplan M1 5.F, Plan 9.1.
export function BuchenPage() {
  const { id = '', jahr: j, monat: m } = useParams();
  const jahr = Number(j);
  const monat = Number(m);
  if (!Number.isInteger(jahr) || jahr < 2000 || jahr > 2100 || !Number.isInteger(monat) || monat < 1 || monat > 12) {
    return <Navigate to={`/kanzlei/mandanten/${id}/belege`} replace />;
  }
  // eigener Zustand je Monat (Auswahl, Entwürfe, Filter)
  return <BuchenMonat key={`${id}/${jahr}/${monat}`} clientId={id} jahr={jahr} monat={monat} />;
}

// Monat laden. Nach einer Aktion an einer Zeile wird nur diese Zeile ersetzt und der Monat im Hintergrund neu geladen
// (Ampeln, Export). Eine Antwort, die vor der letzten eigenen Änderung losgeschickt wurde, wird verworfen.
function useMonat(url: string) {
  const [data, setData] = useState<MonatAntwort>();
  const [fehler, setFehler] = useState<{ text: string; status: number | null } | null>(null);
  const stand = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const laden = useCallback(async () => {
    const s = stand.current;
    try {
      const d = await api.get<MonatAntwort>(url);
      if (stand.current === s) {
        setData(d);
        setFehler(null);
      }
    } catch (err) {
      setFehler({ text: errorText(err), status: err instanceof ApiError ? err.status : null });
    }
  }, [url]);

  useEffect(() => {
    void laden();
    return () => window.clearTimeout(timer.current);
  }, [laden]);

  const aendern = useCallback((f: (d: MonatAntwort) => MonatAntwort) => {
    stand.current++;
    setData((d) => (d ? f(d) : d));
  }, []);
  const ersetzen = useCallback((d: MonatAntwort) => {
    stand.current++;
    setData(d);
    setFehler(null);
  }, []);
  // kurz nach der letzten Aktion einmal neu laden
  const bald = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void laden(), 600);
  }, [laden]);
  return { data, fehler, laden, aendern, ersetzen, bald };
}

// Detail rechts neben der Tabelle erst ab dieser Breite; schmaler (Tablet, Handy) direkt unter der gewählten Zeile
function useBreit(query = '(min-width: 1360px)'): boolean {
  const [breit, setBreit] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const f = () => setBreit(mq.matches);
    mq.addEventListener('change', f);
    return () => mq.removeEventListener('change', f);
  }, [query]);
  return breit;
}

const ohne = <T,>(o: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in o)) return o;
  const n = { ...o };
  delete n[key];
  return n;
};

// Entwürfe im Tab (sessionStorage). Ohne Speicher (gesperrt, voll) geht die Seite trotzdem, die Entwürfe überstehen
// dann nur keinen Seitenwechsel.
const speicherLesen = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const speicherSchreiben = (key: string, wert: string | null) => {
  try {
    if (wert === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, wert);
  } catch {
    // voll oder gesperrt: dann eben ohne
  }
};

const FILTER: { key: Filter; label: string }[] = [
  { key: 'pruefen', label: 'Zu prüfen' },
  { key: 'bestaetigt', label: 'Bestätigt' },
  { key: 'alle', label: 'Alle' },
];

function BuchenMonat({ clientId, jahr, monat }: { clientId: string; jahr: number; monat: number }) {
  const { me } = useKanzlei();
  const toast = useToast();
  const basis = `/api/kanzlei/buchen/${clientId}/${jahr}/${monat}`;
  const mm = String(monat).padStart(2, '0');
  const grenzen = { erster: `${jahr}-${mm}-01`, letzter: `${jahr}-${mm}-${String(new Date(Date.UTC(jahr, monat, 0)).getUTCDate()).padStart(2, '0')}` };
  const mo = useMonat(basis);
  const liste = useKontenliste();
  const breit = useBreit();
  const d = mo.data;

  const [filter, setFilter] = useState<Filter | null>(null);
  const [auswahl, setAuswahl] = useState<string | null>(null);
  // ungespeicherte Änderungen je Zeile (Wechsel der Zeile verliert nichts). Die von Hand stehen auch im Tab
  // (sessionStorage) und kommen nach jedem Seitenwechsel wieder (nur für Zeilen, die es noch gibt und die noch zu
  // prüfen sind, siehe unten).
  const speicherKey = entwurfSchluessel(me.user.id, clientId, jahr, monat);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, EntwurfTeil[]>>(() => entwuerfeLesen(speicherLesen(speicherKey)));
  // Entwürfe, die nur aus einem vorgeschlagenen Beleg bestehen (niemand hat etwas getippt): kein „geändert“, keine
  // Nachfrage beim Verlassen, nicht im Tab gespeichert
  const [auto, setAuto] = useState<Set<string>>(() => new Set());
  const vonHand = Object.keys(entwuerfe).filter((id) => !auto.has(id));
  const handEntwurf = (id: string) => id in entwuerfe && !auto.has(id);
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const busyRef = useRef(false);
  // in dieser Ansicht bearbeitete Zeilen bleiben stehen, auch wenn sie nicht mehr zum Filter passen
  const [behalten, setBehalten] = useState<Set<string>>(() => new Set());
  const [beleg, setBeleg] = useState<string | null>(null);
  const [fokus, setFokus] = useState<{ id: string; n: number } | null>(null);

  const zurueck = `/kanzlei/mandanten/${clientId}/belege?jahr=${jahr}&monat=${monat}`;
  const name = d?.mandant.name;
  useEffect(() => {
    document.title = `Buchen ${monthName(monat)} ${jahr}${name ? ` · ${name}` : ''} · Elbcons Kanzlei`;
  }, [name, jahr, monat]);

  // Beim ersten Laden: Filter „Zu prüfen“, wenn es etwas zu prüfen gibt, und die erste solche Zeile wählen
  useEffect(() => {
    if (!d || filter) return;
    const offen = d.zeilen.some(zuPruefen);
    const f: Filter = offen ? 'pruefen' : 'alle';
    setFilter(f);
    const erste = d.zeilen.find(zuPruefen) ?? d.zeilen[0];
    if (erste) {
      setAuswahl(erste.id);
      // nur, wenn der Cursor noch nirgends steht (nicht in ein Feld springen, ohne zu scrollen)
      if (document.activeElement === document.body) setFokus({ id: erste.id, n: Date.now() });
    }
  }, [d, filter]);

  const aktiverFilter: Filter = filter ?? 'alle';
  const sichtbar = d ? d.zeilen.filter((z) => imFilter(z, aktiverFilter) || behalten.has(z.id)) : [];
  const zeile = auswahl ? d?.zeilen.find((z) => z.id === auswahl) : undefined;

  // gewählte Zeile ist weg (Filter, neu gelesen): erste passende wählen
  useEffect(() => {
    if (!d || !filter || !auswahl) return;
    if (sichtbar.some((z) => z.id === auswahl)) return;
    const erste = sichtbar.find(zuPruefen) ?? sichtbar[0];
    setAuswahl(erste?.id ?? null);
  });

  // Fokus auf eine Zeile der Tabelle, nachdem sie gezeichnet ist
  useEffect(() => {
    if (!fokus) return;
    const el = document.querySelector<HTMLElement>(`tr[data-zeile="${fokus.id}"]`);
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [fokus]);
  const zurZeile = (id: string) => setFokus({ id, n: Date.now() });
  const insFeld = (feld: string) =>
    window.setTimeout(() => document.querySelector<HTMLInputElement>(`.zd [data-feld="${feld}"]`)?.focus(), 0);

  // KI läuft: alle 3 s nachsehen; Meldung, wenn sie fertig ist. Ob sie etwas vorgeschlagen hat, zeigt die Zahl der
  // Zeilen mit KI-Vorschlag: Sie wächst mit jedem Vorschlag (die KI bekommt nur Zeilen ohne ihren Vorschlag).
  const kiStatus = d?.ki.status ?? null;
  const kiZahl = d ? d.zeilen.filter((z) => z.quelleVorschlag === 'ki').length : null;
  useEffect(() => {
    if (kiStatus !== 'laeuft') return;
    const t = window.setInterval(() => void mo.laden(), 3000);
    return () => window.clearInterval(t);
  }, [kiStatus, mo.laden]);
  const kiVorher = useRef(kiStatus);
  const kiZahlVorher = useRef<number | null>(null); // vor dem Lauf
  const kiZahlZuletzt = useRef<number | null>(null); // zuletzt, als sie nicht lief
  useEffect(() => {
    if (kiStatus === 'laeuft' && kiVorher.current !== 'laeuft') kiZahlVorher.current = kiZahlZuletzt.current ?? kiZahl;
    if (kiVorher.current === 'laeuft' && kiStatus === 'fertig') {
      const vorher = kiZahlVorher.current;
      toast(vorher !== null && kiZahl !== null && kiZahl <= vorher ? 'Die KI hatte keine Vorschläge.' : 'KI-Vorschläge sind da.');
    }
    if (kiVorher.current === 'laeuft' && kiStatus === 'fehler') toast('KI-Vorschläge haben nicht geklappt.');
    if (kiStatus !== 'laeuft') kiZahlZuletzt.current = kiZahl;
    kiVorher.current = kiStatus;
  }, [kiStatus, kiZahl, toast]);

  // Entwürfe nur für Zeilen, die es noch gibt und die noch zu prüfen sind (neu gelesen, KI, andere Mitarbeiterin, aus
  // dem Tab zurückgeholt). Ein Entwurf gleich der Zeile ist keiner; ein nur vorgeschlagener Beleg gilt nur, solange die
  // Zeile offen und ohne Buchung ist (sonst verdeckte er den neuen Vorschlag).
  useEffect(() => {
    if (!d) return;
    const zeilen = new Map(d.zeilen.filter(zuPruefen).map((z) => [z.id, z]));
    const bleibt = (id: string, t: EntwurfTeil[]) => {
      const z = zeilen.get(id);
      if (!z || entwurfGleich(t, z)) return false;
      return !auto.has(id) || (z.status === 'offen' && !z.teile.length);
    };
    setEntwuerfe((e) => (Object.entries(e).every(([id, t]) => bleibt(id, t)) ? e : Object.fromEntries(Object.entries(e).filter(([id, t]) => bleibt(id, t)))));
  }, [d, auto]);

  // Entwürfe von Hand im Tab ablegen (keine mehr: Eintrag weg). Erst, wenn der Monat da ist: Bis dahin stehen dort
  // nur die eben gelesenen.
  const geladen = d !== undefined;
  useEffect(() => {
    if (!geladen) return;
    speicherSchreiben(speicherKey, entwuerfeText(Object.fromEntries(Object.entries(entwuerfe).filter(([id]) => !auto.has(id)))));
  }, [geladen, entwuerfe, auto, speicherKey]);

  // Ungespeicherte Änderungen: vor dem Verlassen der Seite nachfragen, beim Schließen des Fensters (Frage des
  // Browsers) und bei jedem Link in der App (Monat wechseln, Zu den Belegen, Navigation). Der Klick wird vor dem
  // Router abgefangen; „Verwerfen“ geht dann dorthin.
  const hatEntwuerfe = vonHand.length > 0;
  const navigate = useNavigate();
  const [verlassen, setVerlassen] = useState<string | null>(null);
  useEffect(() => {
    if (!hatEntwuerfe) return;
    const schliessen = (e: BeforeUnloadEvent) => e.preventDefault();
    const klick = (e: MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!(a instanceof HTMLAnchorElement) || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const ziel = new URL(a.href, window.location.href);
      if (ziel.origin !== window.location.origin || ziel.pathname.startsWith('/api/')) return;
      if (ziel.pathname === window.location.pathname && ziel.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setVerlassen(ziel.pathname + ziel.search + ziel.hash);
    };
    window.addEventListener('beforeunload', schliessen);
    document.addEventListener('click', klick, true);
    return () => {
      window.removeEventListener('beforeunload', schliessen);
      document.removeEventListener('click', klick, true);
    };
  }, [hatEntwuerfe]);

  const teileVon = (z: ZeileSicht) => entwuerfe[z.id] ?? entwurfAus(z);
  const setTeile = (z: ZeileSicht, t: EntwurfTeil[], automatisch = false) => {
    // was schon von Hand getippt ist, wird nie „automatisch“ (sonst ginge es beim Verlassen ohne Frage verloren)
    const auch = automatisch && !handEntwurf(z.id);
    setEntwuerfe((e) => (entwurfGleich(t, z) ? ohne(e, z.id) : { ...e, [z.id]: t }));
    setAuto((a) => (auch === a.has(z.id) ? a : auch ? new Set(a).add(z.id) : new Set([...a].filter((id) => id !== z.id))));
    setFehler((f) => ohne(f, z.id));
  };

  // Aktion an einer Zeile: Antwort ersetzt die Zeile, der Monat lädt im Hintergrund nach.
  // Ergebnis: die neue Zeile, bei Fehler der HTTP-Status (0 = unbekannt), null = es lief schon etwas.
  async function aktion(z: ZeileSicht, pfad: string, body?: unknown, patch = false): Promise<ZeileSicht | number | null> {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(z.id);
    setFehler((f) => ohne(f, z.id));
    try {
      const url = `/api/kanzlei/buchen/zeilen/${z.id}${pfad}`;
      const neu = patch ? await api.patch<ZeileSicht>(url, body) : await api.post<ZeileSicht>(url, body);
      mo.aendern((x) => ({ ...x, zeilen: x.zeilen.map((y) => (y.id === neu.id ? neu : y)) }));
      setEntwuerfe((e) => ohne(e, z.id));
      setBehalten((b) => new Set(b).add(z.id));
      mo.bald();
      return neu;
    } catch (err) {
      // 422: harter Befund, der Satz steht im Detail; Befunde der Zeile neu holen
      setFehler((f) => ({ ...f, [z.id]: errorText(err) }));
      mo.bald();
      return err instanceof ApiError ? err.status : 0;
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  // Teile prüfen (nur wenn geändert oder noch keine Buchung da ist); Fehler → Satz und Cursor ins Feld. Ohne
  // Änderung: was die Seite zeigt (erwartet), damit nichts anderes bestätigt wird, falls sich der Vorschlag
  // inzwischen geändert hat.
  function eingabe(z: ZeileSicht): { teile: TeilEingabe[] } | { erwartet: ErwartetTeil[] } | null {
    const entwurf = entwuerfe[z.id];
    if (!entwurf && z.teile.length) return { erwartet: erwartetAus(z) };
    const p = zuEingabe(entwurf ?? entwurfAus(z), z.betragCents, liste);
    if (!p.ok) {
      setFehler((f) => ({ ...f, [z.id]: p.fehler }));
      insFeld(p.feld);
      return null;
    }
    return { teile: p.teile };
  }

  const weiterNach = (z: ZeileSicht, neu: ZeileSicht) => {
    const liste2 = sichtbar.map((y) => (y.id === neu.id ? neu : y));
    const next = naechsteOffene(liste2, z.id);
    if (next) {
      setAuswahl(next);
      zurZeile(next);
    } else {
      zurZeile(z.id);
      if (!d?.zeilen.some((y) => y.id !== z.id && zuPruefen(y))) toast('Alle Zeilen sind erledigt.');
    }
  };

  async function bestaetigen(z: ZeileSicht) {
    const e = eingabe(z);
    if (!e) return;
    const neu = await aktion(z, '/bestaetigen', e);
    if (typeof neu === 'object' && neu?.status === 'bestaetigt') weiterNach(z, neu);
    // harter Befund: Cursor ins Gegenkonto, damit ein zweites Enter nicht dasselbe noch einmal schickt
    else if (neu === 422) insFeld('gk-0');
    // Vorschlag inzwischen geändert (oder schon bestätigt): Monat neu laden, die Zeile bleibt gewählt, der Satz steht da
    else if (neu === 409) {
      void mo.laden();
      zurZeile(z.id);
    }
  }
  async function speichern(z: ZeileSicht) {
    const e = eingabe(z);
    if (!e || !('teile' in e)) return;
    const neu = await aktion(z, '', { teile: e.teile }, true);
    if (typeof neu === 'object' && neu) zurZeile(z.id);
  }
  // Esc: Entwurf von Hand weg (ein nur vorgeschlagener Beleg ist keine Änderung und bleibt)
  function abbrechen(z: ZeileSicht) {
    if (handEntwurf(z.id)) setEntwuerfe((e) => ohne(e, z.id));
    setFehler((f) => ohne(f, z.id));
    zurZeile(z.id);
  }
  async function zuruecknehmen(z: ZeileSicht) {
    if (typeof (await aktion(z, '/zuruecknehmen')) === 'object') zurZeile(z.id);
  }
  async function verwerfen(z: ZeileSicht, grund: 'doppelt' | 'vorgemerkt') {
    const neu = await aktion(z, '/verwerfen', { grund });
    if (typeof neu === 'object' && neu) weiterNach(z, neu);
  }
  async function keinLohn(z: ZeileSicht) {
    if (typeof (await aktion(z, '/kein-lohn')) === 'object') zurZeile(z.id);
  }

  const waehle = (id: string) => {
    setAuswahl((a) => (a === id && !breit ? null : id));
    zurZeile(id);
  };

  // Tastatur auf der gewählten Zeile: ↑↓ (Pos1/Ende) wählen, Enter bestätigt oder springt ins Gegenkonto,
  // eine Ziffer beginnt das Gegenkonto, Esc verwirft die Änderung
  const taste = (e: KeyboardEvent<HTMLTableRowElement>, z: ZeileSicht) => {
    if (e.target !== e.currentTarget || e.altKey || e.ctrlKey || e.metaKey) return;
    const i = sichtbar.findIndex((y) => y.id === z.id);
    const geh = (n: number) => {
      const y = sichtbar[Math.max(0, Math.min(sichtbar.length - 1, n))];
      if (y) {
        setAuswahl(y.id);
        zurZeile(y.id);
      }
    };
    const offen = zuPruefen(z);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      geh(i + (e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      geh(e.key === 'Home' ? 0 : sichtbar.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (auswahl !== z.id) setAuswahl(z.id);
      if (!offen || busyRef.current) return;
      if (handEntwurf(z.id) || vollstaendig(z)) void bestaetigen(z);
      else {
        const p = zuEingabe(teileVon(z), z.betragCents, liste);
        insFeld(p.ok ? 'gk-0' : p.feld);
      }
    } else if (e.key === 'Escape' && handEntwurf(z.id)) {
      e.preventDefault();
      abbrechen(z);
    } else if (/^\d$/.test(e.key) && offen && !busyRef.current) {
      e.preventDefault();
      setAuswahl(z.id);
      const t = teileVon(z);
      setTeile(z, t.map((x, k) => (k === 0 ? { ...x, gegenkonto: e.key } : x)));
      window.setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('.zd [data-feld="gk-0"]');
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }, 0);
    }
  };

  // Zeile von Hand, Korrektur, Löschen: Antwort ist der ganze Monat (Ampeln und Saldo ändern sich mit)
  const [hand, setHand] = useState<ZeileStart | null>(null);
  const [handFehler, setHandFehler] = useState<string | null>(null);
  const neueZeile = (vorher: MonatAntwort | undefined, neu: MonatAntwort, w: ZeileWerte) => {
    const alt = new Set(vorher?.zeilen.map((y) => y.id));
    const z =
      neu.zeilen.find((y) => !alt.has(y.id) && y.buchungstag === w.datum && y.betragCents === w.betragCents) ??
      neu.zeilen.find((y) => !alt.has(y.id));
    if (z) {
      setBehalten((b) => new Set(b).add(z.id));
      setAuswahl(z.id);
      zurZeile(z.id);
    }
  };
  async function handAnlegen(w: ZeileWerte) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('hand');
    setHandFehler(null);
    try {
      const neu = await api.post<MonatAntwort>(`${basis}/zeilen`, w);
      const vorher = d;
      mo.ersetzen(neu);
      setHand(null);
      neueZeile(vorher, neu, w);
      toast('Zeile von Hand angelegt');
    } catch (err) {
      setHandFehler(errorText(err));
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }
  async function korrigieren(z: ZeileSicht, w: ZeileWerte): Promise<boolean> {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(z.id);
    setFehler((f) => ohne(f, z.id));
    try {
      const neu = await api.post<MonatAntwort>(`/api/kanzlei/buchen/zeilen/${z.id}/korrigieren`, w);
      const vorher = d;
      mo.ersetzen(neu);
      setEntwuerfe((e) => ohne(e, z.id));
      // aus einem Scan entsteht die Zeile neu (neue ID); von Hand bleibt sie
      if (neu.zeilen.some((y) => y.id === z.id)) zurZeile(z.id);
      else neueZeile(vorher, neu, w);
      toast('Zeile geändert');
      return true;
    } catch (err) {
      setFehler((f) => ({ ...f, [z.id]: errorText(err) }));
      return false;
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }
  async function loeschen(z: ZeileSicht) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(z.id);
    try {
      mo.ersetzen(await api.del<MonatAntwort>(`/api/kanzlei/buchen/zeilen/${z.id}`));
      setEntwuerfe((e) => ohne(e, z.id));
      setAuswahl(null);
      toast('Zeile gelöscht');
    } catch (err) {
      setFehler((f) => ({ ...f, [z.id]: errorText(err) }));
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  async function lesen() {
    setBusy('lesen');
    try {
      mo.ersetzen(await api.post<MonatAntwort>(`${basis}/lesen`));
      toast('Auszüge neu gelesen');
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }
  async function kiHolen() {
    setBusy('ki');
    try {
      await api.post(`${basis}/ki`);
      mo.aendern((x) => ({ ...x, ki: { ...x.ki, status: 'laeuft' } }));
    } catch (err) {
      toast(errorText(err));
      // z. B. läuft sie schon (aus einem anderen Fenster): Stand holen, dann zeigt die Seite den Lauf
      void mo.laden();
    } finally {
      setBusy(null);
    }
  }

  // Kopf: Mandant, Monat mit Wechsel, Kennzeichen „Testmandant“
  const t = today();
  const vor = monatSchritt(jahr, monat, -1);
  const nach = monatSchritt(jahr, monat, 1);
  const nachGeht = nach.jahr * 12 + nach.monat <= t.year * 12 + t.month;
  const url = (x: { jahr: number; monat: number }) => `/kanzlei/mandanten/${clientId}/buchen/${x.jahr}/${x.monat}`;
  const kopf = (
    <>
      <div className="crumb">
        <Link to="/kanzlei/mandanten">Mandanten</Link> › <Link to={zurueck}>{name ?? 'Mandant'}</Link> › Buchen
      </div>
      <div className="head bu-head">
        <div>
          <h1>
            Buchen {monthName(monat)} {jahr}
          </h1>
          <p>
            {name ?? '\u00a0'}
            {d?.mandant.number ? ` · Mandanten-Nr. ${d.mandant.number}` : ''}
            {d?.mandant.testdaten && <span className="pill p-warn bu-test">Testmandant</span>}
          </p>
        </div>
        <div className="bu-kopf-r">
          <nav className="bu-monat" aria-label="Monat wechseln">
            <Link className="btn sm" to={url(vor)} aria-label={`Vormonat ${monthName(vor.monat)} ${vor.jahr}`}>
              ‹
            </Link>
            <span>
              {monthName(monat)} {jahr}
            </span>
            {nachGeht ? (
              <Link className="btn sm" to={url(nach)} aria-label={`Nächster Monat ${monthName(nach.monat)} ${nach.jahr}`}>
                ›
              </Link>
            ) : (
              <button type="button" className="btn sm" disabled aria-label="Nächster Monat">
                ›
              </button>
            )}
          </nav>
          <Link className="btn sm" to={zurueck}>
            Zu den Belegen
          </Link>
          {d && (
            <button type="button" className="btn sm" disabled={busy !== null} onClick={() => void lesen()}>
              {busy === 'lesen' ? 'Liest …' : 'Auszüge neu lesen'}
            </button>
          )}
        </div>
      </div>
    </>
  );

  if (!d) {
    return (
      <div className="bu">
        {kopf}
        {mo.fehler ? (
          <div className="lock lk-bad" role="alert">
            <span style={{ flex: 1 }}>{mo.fehler.text}</span>
            {mo.fehler.status !== 403 && mo.fehler.status !== 404 && (
              <button type="button" className="btn sm" onClick={() => void mo.laden()}>
                Erneut laden
              </button>
            )}
          </div>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )}
      </div>
    );
  }

  const aktiv = d.konten.filter((k) => k.status === 'aktiv');
  const anzahl = (f: Filter) => d.zeilen.filter((z) => imFilter(z, f)).length;
  // KI-Vorschläge aus dem Testbetrieb in der Tabelle, oder die KI läuft gerade im Testbetrieb: Hinweis für Testdaten
  const kiZeilen = d.zeilen.some((z) => z.kiTestmodus);
  const kiTest = d.ki.test || kiZeilen;
  const kontoName = (z: ZeileSicht) => d.konten.find((k) => k.id === z.bankkontoId)?.bezeichnung ?? '';

  const detail = zeile && (
    <ZeileDetail
      key={zeile.id}
      zeile={zeile}
      kontoName={kontoName(zeile)}
      teile={teileVon(zeile)}
      geaendert={handEntwurf(zeile.id)}
      setTeile={(x, automatisch) => setTeile(zeile, x, automatisch)}
      liste={liste}
      busy={busy === zeile.id}
      fehler={fehler[zeile.id] ?? null}
      onBestaetigen={() => void bestaetigen(zeile)}
      onSpeichern={() => void speichern(zeile)}
      onAbbrechen={() => abbrechen(zeile)}
      onZuruecknehmen={() => void zuruecknehmen(zeile)}
      onVerwerfen={(g) => void verwerfen(zeile, g)}
      onKeinLohn={() => void keinLohn(zeile)}
      onBeleg={setBeleg}
      erster={grenzen.erster}
      letzter={grenzen.letzter}
      onKorrigieren={(w) => korrigieren(zeile, w)}
      onLoeschen={() => void loeschen(zeile)}
    />
  );

  return (
    <div className="bu">
      {kopf}
      {mo.fehler && (
        <div className="lock" role="status">
          <span style={{ flex: 1 }}>Aktualisieren hat nicht geklappt. Die Angaben sind vielleicht nicht mehr aktuell.</span>
          <button type="button" className="btn sm" onClick={() => void mo.laden()}>
            Erneut laden
          </button>
        </div>
      )}

      {/* Sonderfälle als Karte, nur wenn sie eintreten */}
      {d.konten
        .filter((k) => !k.bestaetigt && k.status === 'aktiv')
        .map((k) => (
          <BankkontoKarte key={k.id} k={k} onFertig={mo.laden} />
        ))}
      {aktiv
        .filter((k) => k.anfangBestaetigen)
        .map((k) => (
          <AnfangKarte key={`anfang-${k.id}`} k={k} url={`${basis}/anfang`} onFertig={mo.ersetzen} />
        ))}
      <DateiHinweise
        dateien={d.dateien}
        onBeleg={setBeleg}
        onNachtragen={
          aktiv.length
            ? (f, u) => {
                setHandFehler(null);
                setHand({ bankkontoId: f.bankkontoId, datum: u.datum, betragCents: u.betragCents, name: u.name, zweck: u.zweck });
              }
            : undefined
        }
      />

      {aktiv.length > 0 ? (
        <div className="bu-konten">
          {aktiv.map((k) => (
            <KontoAmpeln key={k.id} k={k} />
          ))}
        </div>
      ) : (
        // der Satz vom Server: keine Datei, keine lesbare Datei oder alle Bankkonten auf „nicht buchen“
        <div className="lock">{d.export.grund ?? 'Für diesen Monat liegt noch kein Kontoauszug vor.'}</div>
      )}
      {aktiv.length > 0 &&
        (hand ? (
          <section className="card bu-hand">
            <ZeileFormular
              titel="Zeile von Hand"
              knopf="Zeile anlegen"
              start={hand}
              konten={aktiv}
              erster={grenzen.erster}
              letzter={grenzen.letzter}
              busy={busy === 'hand'}
              fehler={handFehler}
              onSenden={(w) => void handAnlegen(w)}
              onAbbrechen={() => setHand(null)}
            />
          </section>
        ) : (
          <p className="bu-hand-l">
            <button
              type="button"
              className="btn sm"
              disabled={busy !== null}
              onClick={() => {
                setHandFehler(null);
                setHand({});
              }}
            >
              + Zeile von Hand
            </button>
            <span className="muted"> etwa für eine Zeile, die im Auszug fehlt oder nicht lesbar war</span>
          </p>
        ))}
      {d.konten
        .filter((k) => k.status === 'nicht_buchen')
        .map((k) => (
          <NichtBuchen key={k.id} k={k} onFertig={mo.laden} />
        ))}
      <DateienListe dateien={d.dateien} onBeleg={setBeleg} />

      {aktiv.length > 0 && d.zeilen.length === 0 && <p className="note">Keine Bankzeilen in diesem Monat.</p>}
      {d.zeilen.length > 0 && (
        <div className={`bu-main${breit ? ' breit' : ''}`}>
          <section className="card bu-liste" aria-label="Bankzeilen buchen">
            <div className="bu-leiste">
              <div className="seg" role="group" aria-label="Filter">
                {FILTER.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={aktiverFilter === f.key}
                    onClick={() => {
                      setFilter(f.key);
                      setBehalten(new Set());
                    }}
                  >
                    {f.label} <span className="bu-n">{anzahl(f.key)}</span>
                  </button>
                ))}
              </div>
              {hatEntwuerfe && (
                <span className="bu-ungesp" role="status">
                  {vonHand.length === 1 ? '1 Zeile geändert, nicht gespeichert' : `${vonHand.length} Zeilen geändert, nicht gespeichert`}
                </span>
              )}
              {d.ki.status === 'laeuft' ? (
                <span className="bu-ki" role="status">
                  <span className="spinner sm" aria-hidden="true" /> KI schlägt vor …
                </span>
              ) : (
                d.ki.moeglich &&
                d.ki.offen > 0 && (
                  <button type="button" className="btn sm bu-kibtn" disabled={busy !== null} onClick={() => void kiHolen()}>
                    KI-Vorschläge holen ({d.ki.offen})
                  </button>
                )
              )}
            </div>
            {(kiZeilen || (d.ki.moeglich && d.ki.offen > 0) || d.ki.status === 'laeuft') && (
              <KiHinweis test={kiTest}>
                Die KI bekommt nur Zeilen ohne Vorschlag, ohne Lohnzahlungen und ohne IBAN. Ihre Vorschläge sind gelb und
                nie „sicher“.
              </KiHinweis>
            )}
            {/* nur, solange die KI noch etwas vorschlagen dürfte: sonst ist der alte Fehler ohne Belang */}
            {d.ki.status === 'fehler' && d.ki.moeglich && d.ki.offen > 0 && (
              <p className="zd-err">{d.ki.grund ?? 'KI-Vorschläge haben nicht geklappt.'}</p>
            )}
            {sichtbar.length ? (
              <BuchenTabelle
                zeilen={sichtbar}
                konten={d.konten}
                auswahl={auswahl}
                geaendert={new Set(vonHand)}
                inline={!breit}
                detail={detail}
                onWaehle={waehle}
                onTaste={taste}
              />
            ) : (
              <p className="note bu-leer">{aktiverFilter === 'pruefen' ? 'Nichts mehr zu prüfen.' : 'Noch keine Zeile bestätigt.'}</p>
            )}
            <p className="note bu-tasten">
              <kbd>↑</kbd> <kbd>↓</kbd> Zeile wählen · <kbd>Enter</kbd> Gegenkonto, St-Sl, bestätigen · <kbd>Esc</kbd> abbrechen ·
              Ziffer tippen: Gegenkonto ändern
            </p>
          </section>
          {breit && <aside className="card bu-det">{detail ?? <p className="note">Zeile wählen.</p>}</aside>}
        </div>
      )}

      <section className="card bu-re" aria-labelledby="bu-re-h">
        <div className="ch">
          <h3 id="bu-re-h">Rechnungen aus dem Rechnungsprogramm</h3>
          <span>Debitor an Erlös · nur Anzeige, kommt mit in den Export</span>
        </div>
        {d.rechnungen.length ? (
          <RechnungenTabelle rechnungen={d.rechnungen} onBeleg={setBeleg} />
        ) : (
          <p className="note">Keine Rechnungen in diesem Monat.</p>
        )}
      </section>

      <ExportLeiste d={d} isAdmin={me.user.isAdmin} onNeu={mo.laden} />

      {beleg && <BelegDialog id={beleg} onClose={() => setBeleg(null)} />}
      <ConfirmDialog
        open={verlassen !== null}
        title="Nicht gespeicherte Änderungen verwerfen?"
        confirmLabel="Verwerfen"
        cancelLabel="Hierbleiben"
        danger
        onConfirm={() => {
          // verworfen heißt verworfen: auch nicht aus dem Tab zurückholen
          speicherSchreiben(speicherKey, null);
          setEntwuerfe({});
          if (verlassen) navigate(verlassen);
          setVerlassen(null);
        }}
        onCancel={() => setVerlassen(null)}
      />
    </div>
  );
}
